export const toCamelCase = (str: string): string => {
	const words = str.split(/[^a-zA-Z0-9]+/).filter(Boolean);
	if (!words[0]) return "";
	return (
		words[0].toLowerCase() +
		words
			.slice(1)
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
			.join("")
	);
};

export const capitalizeWord = (string: string) => {
	if (!string) return "";
	return string.charAt(0).toUpperCase() + string.slice(1);
};

export const formatDateToLocalISOString = (date: Date): string => {
	if (Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	const hours = `${date.getHours()}`.padStart(2, "0");
	const minutes = `${date.getMinutes()}`.padStart(2, "0");
	const seconds = `${date.getSeconds()}`.padStart(2, "0");
	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const formatDateToYYYYMMDD = (date: Date | string): string => {
	const dateObj = date instanceof Date ? date : new Date(date);
	if (Number.isNaN(dateObj.getTime())) return "";
	const year = dateObj.getFullYear();
	const month = `${dateObj.getMonth() + 1}`.padStart(2, "0");
	const day = `${dateObj.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

export const formatDateToMMDDYYYY = (date: Date | string): string => {
	const dateObj = date instanceof Date ? date : new Date(date);
	if (Number.isNaN(dateObj.getTime())) return "";
	const year = dateObj.getFullYear();
	const month = `${dateObj.getMonth() + 1}`.padStart(2, "0");
	const day = `${dateObj.getDate()}`.padStart(2, "0");
	return `${month}/${day}/${year}`;
};

export const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(amount);
};

export const formatNumber = (value: number): string => {
	return new Intl.NumberFormat("en-US").format(value);
};

export const removeCommaFromInt = (number: string): string => {
	return number.replaceAll(",", "");
};

export const formatPhoneNumber = (phone: string): string => {
	if (!phone) return "";

	// Remove all non-digit characters
	const digits = phone.replaceAll(/\D/g, "");

	// Format as (XXX) XXX-XXXX if we have 10 digits
	if (digits.length === 10) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}

	// Return original if it doesn't match expected format
	return phone;
};

/**
 * Strips display formatting so outbound phone values stay in the canonical
 * digits-only representation the APIs already use (see the address-book save
 * path and `stripPhoneMask` in `format-quote-state.ts`). Display formatting is
 * applied at hydration, so normalizing here keeps the payload identical whether
 * a number arrived from a bulk upload, was hydrated, or was typed behind a mask.
 */
export const stripPhoneFormatting = (phone: string): string =>
	phone.replaceAll(/\D/g, "");

export const isValidPhoneNumber = (phone: string): boolean => {
	if (!phone) return false;

	// Remove all non-digit characters
	const digits = phone.replaceAll(/\D/g, "");

	// Check if we have exactly 10 digits
	return digits.length === 10;
};

export const normalizeString = (str: string): string => {
	const normalizedStr = str
		.normalize("NFD") // Decompose accented characters
		.replaceAll(/[\u0300-\u036F]/g, "") // Remove diacritical marks
		.replaceAll(/[_-]/g, " ") // Remove underscores and hyphens
		.toLowerCase();
	return capitalizeWord(normalizedStr);
};

export const camelToTitle = (input: string): string => {
	if (!input) return "";

	// Replace common separators with spaces
	const withSpaces = input.replaceAll(/[_-]+/g, " ");

	// Insert a space before capital letters that follow lower-case letters or numbers
	const splitCamel = withSpaces.replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2");

	// Split on spaces, trim and capitalize each word
	const words = splitCamel
		.split(/\s+/)
		.filter(Boolean)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

	return words.join(" ");
};

/** Split camelCase into words, preserving all-caps acronyms (e.g. "NMFC" stays "NMFC"). */
export const camelToTitlePreserveAcronyms = (input: string): string => {
	if (!input) return "";
	return input
		.replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replaceAll(/([A-Z])(?=[A-Z][a-z])/g, "$1 ")
		.trim();
};

export const capToTitle = (input: string): string => {
	if (!input) return "";

	// Replace common separators with spaces
	const withSpaces = input.replaceAll(/[_-]+/g, " ");

	// Split on spaces, trim and capitalize each word
	const words = withSpaces
		.split(/\s+/)
		.filter(Boolean)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

	return words.join(" ");
};
